import * as THREE from 'three';

import { Materials, iconOf } from '../game/buildings/Materials';
import { screenPlacer } from './ScreenPoint';

/**
 * Co ruszyło z budynku, nad budynkiem.
 *
 * HTML nad kanwą, po raz kolejny i z tego samego powodu, co nazwy graczy
 * i paski budowy: gra rysuje w mały bufor skalowany całkowicie, więc liczba
 * wrysowana w świat wychodzi rozmazana. To jest interfejs, nie świat.
 *
 * **Chmurka mówi o ruchu, nie o stanie.** Ile budynek ma teraz, mówi dymek pod
 * kursorem i pasek magazynu; tu pokazuje się wyłącznie to, co właśnie z niego
 * wyszło — i dlatego rzecz znika sama po trzech sekundach, zamiast czekać na
 * cokolwiek, co miałoby ją zdjąć.
 *
 * **Znak jest po stronie gracza, nie budynku.** Zbiór idzie do sakwy, więc ma
 * plus; wóz wywozi towar z magazynu, więc ma minus. Jedno i drugie jest ruchem
 * własnego majątku i dlatego wygląda tak samo poza znakiem.
 *
 * **Widzi je tylko właściciel** i nie jest to obietnica tego pliku: zbiór wraca
 * z żądania, które wysłał ten klient, a wysyłka wozu przychodzi wiadomością
 * kierowaną do jednego gracza. Nie ma tu czego filtrować, bo nic cudzego tu nie
 * dociera.
 */

/** Ile chmurka żyje, w milisekundach. */
const LIFE_MS = 3000;

/**
 * Odstęp między kolejnymi chmurkami jednego zbioru.
 *
 * Zbiór potrafi wynieść kilka surowców naraz. Puszczone w jednej chwili stają
 * jedna na drugiej i czytają się jak jeden napis; puszczone schodkowo czytają
 * się jako kilka rzeczy, które wyszły — a ta różnica jest całym powodem, dla
 * którego każdy surowiec dostaje własną chmurkę zamiast wiersza we wspólnej.
 */
const STEP_MS = 260;

/** Minus jak w druku, nie łącznik z klawiatury. */
const MINUS = '−';

interface Pop {
  el: HTMLElement;
  /** Punkt w świecie, nad którym wisi — szczyt sylwetki budynku. */
  anchor: THREE.Vector3;
  /** Kiedy ją sprzątnąć, zegarem tego klienta. */
  until: number;
}

export class YieldPops {
  private readonly root = document.createElement('div');
  private readonly live: Pop[] = [];

  constructor(
    private readonly container: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly materials: Materials
  ) {
    this.root.className = 'yield-pops';
    this.container.appendChild(this.root);
  }

  /**
   * Pokaż, co ruszyło spod tego punktu.
   *
   * Kotwica jest kopiowana, bo wywołujący dostaje ją z pola budynków i nie ma
   * powodu, żeby trzymał ją dla nas przez trzy sekundy.
   */
  show(
    anchor: THREE.Vector3,
    moved: { material: string; amount: number }[],
    sign: 'gain' | 'loss'
  ): void {
    const now = performance.now();
    let step = 0;

    for (const item of moved) {
      if (item.amount <= 0) continue;

      const el = document.createElement('div');
      el.className = 'yield-pop';

      // Napis siedzi w dziecku, bo transform rodzica niesie pozycję na ekranie
      // i jest przepisywany co klatkę. Animacja na tym samym elemencie byłaby
      // przez to co klatkę kasowana — stąd dwa elementy, jeden na miejsce
      // i jeden na ruch.
      const text = document.createElement('span');
      text.className = 'yield-pop-text';
      text.textContent = `${sign === 'gain' ? '+' : MINUS}${item.amount} ${iconOf(
        this.materials,
        item.material
      )}`;
      // Opóźnienie robi schodek, a `backwards` trzyma chmurkę niewidoczną,
      // dopóki nie przyjdzie jej kolej: pierwsza klatka animacji ma zerową
      // przezroczystość, więc nie ma tu czego chować osobno.
      text.style.animationDelay = `${step}ms`;
      el.appendChild(text);

      this.root.appendChild(el);
      this.live.push({ el, anchor: anchor.clone(), until: now + step + LIFE_MS });
      step += STEP_MS;
    }
  }

  /**
   * Przepisz pozycje i sprzątnij to, co dożyło swojego.
   *
   * Wołane po renderze, jak nazwy graczy: budynek stoi w miejscu, ale kamera
   * nie, więc punkt na ekranie zmienia się co klatkę.
   */
  update(camera: THREE.OrthographicCamera): void {
    if (this.live.length === 0) return;

    const now = performance.now();
    const place = screenPlacer(this.container, this.canvas, camera);

    for (let index = this.live.length - 1; index >= 0; index -= 1) {
      const pop = this.live[index];

      if (now >= pop.until) {
        pop.el.remove();
        this.live.splice(index, 1);
        continue;
      }

      // Poza kadrem albo za kamerą: schowana, a nie dociśnięta do krawędzi,
      // gdzie mówiłaby o budynku, którego tam nie ma.
      const at = place(pop.anchor);
      pop.el.style.display = at ? 'block' : 'none';
      if (at) {
        pop.el.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px)`;
      }
    }
  }

  dispose(): void {
    for (const pop of this.live) pop.el.remove();
    this.live.length = 0;
    this.root.remove();
  }
}
